import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ImageGallery from '../../components/ImageGallery';
import ProductCTA from '../../components/ProductCTA';
import './EquipmentInventory.css';

// Import equipment data
import equipmentData from '../../data/equipment.json';

// Dynamic image imports for equipment inventory
const imageModules = import.meta.glob('../../assets/products/equipment-inventory/*.jpg', { eager: true }) as Record<string, { default: string }>;

// Helper to get image URL from filename
const getImageUrl = (filename: string): string => {
  const key = Object.keys(imageModules).find(k => k.endsWith(`/${filename}`));
  return key ? imageModules[key].default : '';
};

interface EquipmentItem {
  id: string;
  stockNumber: string;
  title: string;
  category: string;
  condition: string;
  specifications: Record<string, string>;
  description: string;
  image: string;
}

const EquipmentInventory = () => {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCondition, setFilterCondition] = useState('all');
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItem[]>([]);

  useEffect(() => {
    // Load equipment data and resolve image paths
    // Cast through unknown to handle JSON's inferred optional properties
    const rawItems = equipmentData as unknown as EquipmentItem[];
    const items = rawItems.map(item => ({
      ...item,
      image: getImageUrl(item.image)
    }));
    setEquipmentItems(items);
  }, []);

  const categories = [
    { value: 'all', label: 'All Equipment' },
    { value: 'roll-formers', label: 'Roll Formers' },
    { value: 'uncoilers', label: 'Uncoilers' },
    { value: 'presses', label: 'Presses' },
    { value: 'complete-systems', label: 'Complete Systems' }
  ];

  const conditions = [
    { value: 'all', label: 'All Conditions' },
    { value: 'new', label: 'New' },
    { value: 'demo', label: 'Demo/Like New' },
    { value: 'used', label: 'Used' }
  ];

  const filteredEquipment = equipmentItems.filter(item => {
    const categoryMatch = filterCategory === 'all' || item.category === filterCategory;
    const conditionMatch = filterCondition === 'all' || item.condition === filterCondition;
    return categoryMatch && conditionMatch;
  });

  const allImages = equipmentItems.map(item => ({
    src: item.image,
    alt: item.title,
    title: `${item.stockNumber}: ${item.title}`
  }));

  return (
    <div className="equipment-inventory-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/products">Products</Link> / Equipment Inventory
        </nav>

        {/* Page Header */}
        <div className="page-header">
          <h1>Equipment Inventory</h1>
          <p className="page-subtitle">
            Quality new and used roll forming equipment available for immediate delivery
          </p>
        </div>

        {/* Overview Section */}
        <section className="overview-section">
          <div className="overview-content">
            <h2>Available Equipment</h2>
            <p>
              Browse our current inventory of new and used roll forming equipment. All used equipment 
              is thoroughly inspected and refurbished to ensure reliable performance. Demo models 
              offer like-new condition at significant savings.
            </p>
            <p>
              Each piece of equipment includes detailed specifications and can be customized with 
              additional tooling or modifications to meet your specific requirements.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="filters-section">
          <div className="filters-container">
            <div className="filter-group">
              <label htmlFor="category-filter">Equipment Type:</label>
              <select
                id="category-filter"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="condition-filter">Condition:</label>
              <select
                id="condition-filter"
                value={filterCondition}
                onChange={(e) => setFilterCondition(e.target.value)}
                className="filter-select"
              >
                {conditions.map(cond => (
                  <option key={cond.value} value={cond.value}>{cond.label}</option>
                ))}
              </select>
            </div>
            <div className="results-count">
              Showing {filteredEquipment.length} of {equipmentItems.length} items
            </div>
          </div>
        </section>

        {/* Equipment Grid */}
        <section className="equipment-grid">
          {filteredEquipment.map((item) => (
            <div key={item.id} className="equipment-card">
              <div className="equipment-image">
                <img
                  src={item.image}
                  alt={item.title}
                  onDoubleClick={() => setModalImage(item.image)}
                  style={{ cursor: 'pointer' }}
                />
                <div className={`condition-badge condition-${item.condition}`}>
                  {item.condition === 'new' ? 'NEW' :
                   item.condition === 'demo' ? 'DEMO' : 'USED'}
                </div>
              </div>
              
              <div className="equipment-details">
                <div className="equipment-header">
                  <h3>{item.stockNumber}</h3>
                </div>
                
                <h4>{item.title}</h4>
                <p className="description">{item.description}</p>
                
                <div className="specifications">
                  <h5>Specifications:</h5>
                  <ul>
                    {Object.entries(item.specifications).map(([key, value]) => (
                      <li key={key}>
                        <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {value}
                      </li>
                    ))}
                  </ul>
                </div>
                
              </div>
            </div>
          ))}
        </section>

        {/* Equipment Gallery */}
        <section className="gallery-section">
          <h2>Equipment Gallery</h2>
          <p>Browse all available equipment in our inventory.</p>
          <ImageGallery images={allImages} columns={4} />
        </section>

        {/* CTA Section */}
        <ProductCTA variant="sales" />
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div
          className="image-modal"
          onClick={() => setModalImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
        >
          <img
            src={modalImage}
            alt="Full size view"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EquipmentInventory;